import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClients, useContracts, useConvenios } from '../../contexts/DataContext';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Input } from '../../components/input';
import { Button } from '../../components/button';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { FianzaView } from '../../components/FianzaView';
import { ContratoView } from '../../components/ContratoView';
import { getFianzaStatus } from '../../utils/convenioCompliance';
import { usePortalConfig } from '../../hooks/usePortalConfig';
import { formatNumeroFianza } from '../../utils/calculations';
import SaldosClienteCard from '../../components/SaldosClienteCard';
import { usePortalView } from '../../contexts/PortalViewContext';

const ClientPortalFianzas = () => {
  const { clientsList, clientsById, clientsLoading } = useClients();
  const { contractsList, contractsLoading } = useContracts();
  const { conveniosById } = useConvenios();
  const { userData } = useUser();
  const { activeClienteId } = usePortalView();
  const portalConfig = usePortalConfig();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterClientId, setFilterClientId] = useState('all');
  const [filterEstatus, setFilterEstatus] = useState('all');
  const [filterPago, setFilterPago] = useState('all');
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);

  const loading = clientsLoading || contractsLoading;

  // The Empresa page owns the fiado's fianzas now — an active fiado context
  // lands on its Fianzas tab (carrying an ?estatus= deep-link filter along).
  // The no-context fallback keeps the old aggregated table.
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  useEffect(() => {
    if (activeClienteId) {
      const estatus = urlParams.get('estatus');
      navigate(
        `/app/portal/cliente/${activeClienteId}?tab=fianzas${estatus ? `&estatus=${encodeURIComponent(estatus)}` : ''}`,
        { replace: true }
      );
    }
  }, [activeClienteId, navigate, urlParams]);

  const resetGuardRef = useRef(true);
  useEffect(() => {
    if (resetGuardRef.current) { resetGuardRef.current = false; return; }
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, filterClientId, filterEstatus, filterPago]);

  // Scoped to the active fiado context: one empresa, no convenio contracts
  // mixed in. Without a cliente context (legacy direct URL) it falls back to
  // the aggregated behavior.
  const scopedClients = useMemo(
    () => (activeClienteId ? clientsList.filter((c) => c.id === activeClienteId) : clientsList),
    [clientsList, activeClienteId]
  );

  const clientNames = useMemo(() => {
    const names = {};
    scopedClients.forEach((c) => { names[c.id] = c.nombreEmpresa || 'Sin nombre'; });
    return names;
  }, [scopedClients]);

  const clientIdSet = useMemo(() => new Set(scopedClients.map((c) => c.id)), [scopedClients]);

  // Check if a contract is accessible (client or convenio)
  const isAccessibleContract = useCallback((contrato) => {
    if (contrato.clientId && clientIdSet.has(contrato.clientId)) return true;
    if (activeClienteId) return false;
    if (contrato.convenioId) {
      const access = userData?.convenioAccess?.[contrato.convenioId];
      if (!access) return false;
      if (access.fullAccess) return true;
      if (contrato.proyectoId && (access.proyectoIds || []).includes(contrato.proyectoId)) return true;
    }
    return false;
  }, [clientIdSet, activeClienteId, userData?.convenioAccess]);

  const getContractParentName = useCallback((contrato) => {
    if (contrato.convenioId) {
      const conv = conveniosById[contrato.convenioId];
      const ben = (conv?.beneficiarios || []).find((b) => b.id === contrato.beneficiarioId);
      return `${conv?.nombre || 'Convenio'} ${ben ? `- ${ben.nombre}` : ''}`;
    }
    return clientNames[contrato.clientId] || 'Sin nombre';
  }, [conveniosById, clientNames]);

  // Build fianzas list from all accessible contracts (client + convenio)
  const fianzasData = useMemo(() => {
    const now = Date.now();
    let fianzas = [];

    contractsList.forEach((contrato) => {
      if (!isAccessibleContract(contrato)) return;
      (contrato.fianzas || []).forEach((fianza) => {
        const estatusCalculado = getFianzaStatus(fianza, now).code;
        fianzas.push({
          ...fianza,
          contratoId: contrato.id,
          clientId: contrato.clientId,
          convenioId: contrato.convenioId,
          clientName: getContractParentName(contrato),
          contratoTitle: contrato.tituloContrato?.value || 'Sin título',
          numeroPoliza: formatNumeroFianza(fianza, 'N/A'),
          tipoFianza: fianza.tipo || fianza.tipoFianza || 'N/A',
          estatusCalculado,
        });
      });
    });

    // Apply filters
    if (filterClientId !== 'all') {
      fianzas = fianzas.filter((f) => f.clientId === filterClientId);
    }
    if (filterEstatus !== 'all') {
      fianzas = fianzas.filter((f) => f.estatusCalculado === filterEstatus);
    }
    if (filterPago !== 'all') {
      const isPagada = filterPago === 'pagada';
      fianzas = fianzas.filter((f) => !!f.pagada === isPagada);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      fianzas = fianzas.filter((f) =>
        f.clientName.toLowerCase().includes(s) ||
        f.contratoTitle.toLowerCase().includes(s) ||
        f.tipoFianza.toLowerCase().includes(s) ||
        f.numeroPoliza.toLowerCase().includes(s)
      );
    }

    return fianzas;
  }, [contractsList, isAccessibleContract, getContractParentName, filterClientId, filterEstatus, filterPago, searchTerm]);

  // Pending fianzas
  const pendingFianzas = useMemo(() => {
    const pending = [];
    contractsList.forEach((contrato) => {
      if (!isAccessibleContract(contrato)) return;
      const requeridas = contrato.fianzasRequeridas?.value || [];
      const emitidas = (contrato.fianzas || []).map((f) => f.tipo || f.tipoFianza);
      requeridas.forEach((req) => {
        if (typeof req === 'object' && req.fianzaId) return; // already linked to emitted
        const tipoStr = typeof req === 'string' ? req : req.tipo || '';
        if (!emitidas.includes(tipoStr)) {
          pending.push({
            tipo: tipoStr,
            clientId: contrato.clientId,
            contratoId: contrato.id,
            clientName: getContractParentName(contrato),
            contratoTitle: contrato.tituloContrato?.value || 'Sin título',
          });
        }
      });
    });
    return pending;
  }, [contractsList, isAccessibleContract, getContractParentName]);

  // Clamp page
  useEffect(() => {
    if (loading) return;
    if (fianzasData.length === 0) return;
    const maxPage = Math.max(1, Math.ceil(fianzasData.length / itemsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [loading, fianzasData.length, itemsPerPage, currentPage]);

  const handleFianzaClick = (fianza) => {
    setSelectedFianza(fianza);
    setIsFianzaViewOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(fianzasData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFianzas = fianzasData.slice(startIndex, endIndex);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <Heading level={1} className="text-azul-surelia mb-2">Fianzas</Heading>
        <Text className="text-zinc-600">
          {fianzasData.length} fianza{fianzasData.length !== 1 ? 's' : ''}
          {pendingFianzas.length > 0 && ` · ${pendingFianzas.length} pendiente${pendingFianzas.length !== 1 ? 's' : ''} de emisión`}
        </Text>
      </div>

      {/* Saldos con afianzadoras — org-gated (showSaldos portal switch). One
          card per cliente the viewer can see; read-only for client users. */}
      {portalConfig.showSaldos && (
        <div className="mb-6 space-y-4">
          {scopedClients.map((cliente) => {
            const contratosCliente = contractsList.filter((c) => c.clientId === cliente.id);
            if (contratosCliente.length === 0) return null;
            return (
              <div key={cliente.id}>
                {scopedClients.length > 1 && (
                  <Text className="text-xs text-zinc-500 mb-1">{cliente.nombreEmpresa}</Text>
                )}
                <SaldosClienteCard contracts={contratosCliente} clientId={cliente.id} />
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-zinc-200 p-3 sm:p-4">
        <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 sm:items-center sm:flex-wrap">
          {scopedClients.length > 1 && (
            <select
              value={filterClientId}
              onChange={(e) => setFilterClientId(e.target.value)}
              className="col-span-2 px-3 py-2 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm"
            >
              <option value="all">Todas las empresas</option>
              {scopedClients.map((c) => (
                <option key={c.id} value={c.id}>{c.nombreEmpresa || 'Sin nombre'}</option>
              ))}
            </select>
          )}

          <select
            value={filterEstatus}
            onChange={(e) => setFilterEstatus(e.target.value)}
            className="px-3 py-2 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm"
          >
            <option value="all">Estatus: Todos</option>
            <option value="vigente">Vigente</option>
            <option value="pendiente">Pendiente</option>
            <option value="vencida">Vencida</option>
          </select>

          <select
            value={filterPago}
            onChange={(e) => setFilterPago(e.target.value)}
            className="px-3 py-2 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm"
          >
            <option value="all">Pago: Todos</option>
            <option value="pagada">Pagada</option>
            <option value="pendiente">Pendiente</option>
          </select>

          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-3 py-2 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm"
          >
            <option value={5}>5 por pág.</option>
            <option value={10}>10 por pág.</option>
            <option value={20}>20 por pág.</option>
            <option value={50}>50 por pág.</option>
          </select>

          <Input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="col-span-2 sm:flex-1 sm:max-w-xs"
          />
        </div>
      </div>

      {/* Fianzas table */}
      <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-x-auto">
        <div className="overflow-x-auto">
          <Table>
            <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
              <TableRow>
                {clientsList.length > 1 && (
                  <TableHeader className="text-white font-semibold py-4">Empresa</TableHeader>
                )}
                <TableHeader className="text-white font-semibold py-4">Número de Fianza</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Monto Afianzado</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Vigencia</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Estatus</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Pago</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Tipo de Fianza</TableHeader>
                <TableHeader className="text-white font-semibold py-4">Contrato</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFianzas.map((fianza, idx) => {
                return (
                  <TableRow
                    key={`${fianza.contratoId}-${fianza.id || idx}`}
                    className={`cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                    }`}
                    onClick={() => handleFianzaClick(fianza)}
                  >
                    {clientsList.length > 1 && (
                      <TableCell className="py-4">
                        <Text className="text-sm font-medium text-azul-surelia">
                          {fianza.clientName}
                        </Text>
                      </TableCell>
                    )}
                    <TableCell className="py-4">
                      <Text className="text-sm text-zinc-700 font-mono">
                        {fianza.numeroPoliza}
                      </Text>
                    </TableCell>
                    <TableCell className="py-4">
                      <Text className="text-sm font-semibold text-verde-lima">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fianza.montoAfianzado || 0)}
                      </Text>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="text-xs">
                        <div className="text-zinc-600">
                          <span className="font-medium">Inicio:</span>{' '}
                          {fianza.inicioVigenciaFianza
                            ? new Date(fianza.inicioVigenciaFianza).toLocaleDateString('es-MX')
                            : 'N/A'}
                        </div>
                        <div className="text-zinc-600">
                          <span className="font-medium">Fin:</span>{' '}
                          {fianza.finVigenciaFianza
                            ? new Date(fianza.finVigenciaFianza).toLocaleDateString('es-MX')
                            : 'N/A'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                        fianza.estatusCalculado === 'vigente' ? 'bg-green-100 text-green-800' :
                        fianza.estatusCalculado === 'vencida' ? 'bg-red-100 text-red-800' :
                        fianza.estatusCalculado === 'liberada' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {fianza.estatusCalculado.charAt(0).toUpperCase() + fianza.estatusCalculado.slice(1)}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                        fianza.pagada ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {fianza.pagada ? 'Pagada' : 'Pendiente'}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <Text className="text-sm font-semibold text-zinc-900">
                        {fianza.tipoFianza}
                      </Text>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="max-w-xs">
                        <Text
                          className="text-sm text-zinc-900 truncate"
                          title={fianza.contratoTitle}
                        >
                          {fianza.contratoTitle}
                        </Text>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedFianzas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={clientsList.length > 1 ? 8 : 7} className="py-8 text-center text-zinc-500">
                    No se encontraron fianzas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {fianzasData.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-zinc-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-zinc-600">
              Mostrando {startIndex + 1} - {Math.min(endIndex, fianzasData.length)} de {fianzasData.length} fianzas
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = 5;
                  const tp = Math.max(1, totalPages);
                  if (tp <= maxVisiblePages) {
                    for (let i = 1; i <= tp; i++) pageNumbers.push(i);
                  } else {
                    pageNumbers.push(1);
                    if (currentPage <= 2) {
                      for (let i = 2; i <= Math.min(3, tp - 1); i++) pageNumbers.push(i);
                      if (tp > 3) pageNumbers.push('...');
                    } else if (currentPage >= tp - 1) {
                      if (tp > 3) pageNumbers.push('...');
                      for (let i = Math.max(tp - 2, 2); i <= tp - 1; i++) pageNumbers.push(i);
                    } else {
                      pageNumbers.push('...');
                      pageNumbers.push(currentPage);
                      pageNumbers.push('...');
                    }
                    if (tp > 1) pageNumbers.push(tp);
                  }
                  return pageNumbers.map((page, i) =>
                    page === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-zinc-400">...</span>
                    ) : (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        color={currentPage === page ? 'surelia' : 'white'}
                        className="text-sm px-3 py-2 min-w-[40px]"
                      >
                        {page}
                      </Button>
                    )
                  );
                })()}
              </div>
              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Siguiente
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Fianzas */}
      {pendingFianzas.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-zinc-200 p-6">
          <Heading level={3} className="text-azul-surelia mb-4">Fianzas Pendientes de Emisión</Heading>
          <div className="space-y-2">
            {pendingFianzas
              .filter((pf) => filterClientId === 'all' || pf.clientId === filterClientId)
              .map((pf, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    const contrato = contractsList.find((c) => c.id === pf.contratoId);
                    if (contrato) { setSelectedContrato(contrato); setIsContratoViewOpen(true); }
                  }}
                  className="w-full flex items-center justify-between py-2 px-3 bg-beige-suave/15 rounded-lg hover:bg-azul-surelia/5 transition-colors text-left"
                >
                  <div>
                    <Text className="text-sm font-medium text-zinc-900">{pf.tipo}</Text>
                    <Text className="text-xs text-zinc-500">
                      Contrato: {pf.contratoTitle}
                      {clientsList.length > 1 && ` · ${pf.clientName}`}
                    </Text>
                  </div>
                  <span className="px-3 py-1 inline-flex text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                    Pendiente de emisión
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* FianzaView Modal */}
      {selectedFianza && (
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={() => { setIsFianzaViewOpen(false); setSelectedFianza(null); }}
          fianza={selectedFianza}
          clientData={clientsById[selectedFianza.clientId]}
          contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId)}
          onUpdate={() => {}}
        />
      )}

      {/* ContratoView Modal */}
      {selectedContrato && (
        <ContratoView
          isOpen={isContratoViewOpen}
          onClose={() => { setIsContratoViewOpen(false); setSelectedContrato(null); }}
          contrato={selectedContrato}
          clientData={clientsById[selectedContrato.clientId]}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
};

export default ClientPortalFianzas;
